/**
 * QL-375 / QL-664 — the "Permitted allocation" box: a header line (title with
 * what the dot measures in brackets · the mandate's name · its chips · the
 * verdict badge), one row per mandate line (status icon, name with "— not
 * held", range bar, weight, band), then the legend with the verdict sentence
 * on its right.
 */

import { formatMandatePct } from "@repo/shared";
import type {
  GoalMandateEvaluation,
  MandateEvaluatedLine,
  MandateScope,
  MandateTemplateMode,
  MandateUnholdableReason,
  Translated,
} from "@repo/shared/types";
import { CheckCircle2, MinusCircle, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./mandate-lines-table.i18n";
import { MandateRangeBar } from "./mandate-range-bar";

/** The sentence on the legend row: what the panel concluded, in one line. */
export type MandateVerdictSentence = {
  text: string;
  tone: "breach" | "ok" | "warning" | "muted";
};

type Props = {
  mode: MandateTemplateMode;
  lines: MandateEvaluatedLine[];
  unlisted: GoalMandateEvaluation["unlisted"];
  scope: MandateScope;
  /** What the dot measures, printed in brackets after the title: "Portfolio" / "For purchases". */
  titleSuffix: string;
  mandateName: string;
  /** Chips between the name and the badge — the mode, then the source when it is the bank's. */
  chips: string[];
  badge: ReactNode;
  /** Group 8 — one muted line under the header: "Replaces {bank mandate} at the bank". */
  subHeader?: ReactNode;
  verdict?: MandateVerdictSentence;
  /** QL-677 — the Mandate selection section, between the legend and the decision box. */
  selectionSection?: ReactNode;
  /** Group 5 — the deviation decision box, under the legend (screen 02). */
  decisionSection?: ReactNode;
  /** False before anything was judged: the figures stay neutral (no green "within"). */
  evaluated?: boolean;
  /**
   * QL-677 — no mandate yet: the rows are the portfolio's own weights (dot
   * only, no band, no bound figure, neutral icon) and the legend says so.
   */
  noMandate?: boolean;
};

function unholdableText(reason: MandateUnholdableReason, t: Translated<typeof T>): string {
  switch (reason) {
    case "no_isin":
      return t.unholdableNoIsin;
    case "no_product":
      return t.unholdableNoProduct;
    case "inactive":
      return t.unholdableInactive;
    case "not_on_platform":
      return t.unholdableNotOnPlatform;
  }
}

function StatusIcon({ line, neutral }: { line: MandateEvaluatedLine; neutral?: boolean }) {
  if (neutral) return <MinusCircle className="h-4 w-4 text-muted-foreground shrink-0" />;
  return line.status === "within" ? (
    <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
  ) : (
    <XCircle className="h-4 w-4 text-destructive shrink-0" />
  );
}

const VERDICT_TONE: Record<MandateVerdictSentence["tone"], string> = {
  breach: "text-destructive",
  ok: "text-muted-foreground",
  warning: "text-warning",
  muted: "text-muted-foreground",
};

export function MandateLinesTable({
  mode,
  lines,
  unlisted,
  scope,
  titleSuffix,
  mandateName,
  chips,
  badge,
  subHeader,
  verdict,
  selectionSection,
  decisionSection,
  noMandate = false,
  evaluated = true,
}: Props) {
  const t = useT(T);
  return (
    <div className="@container rounded-lg border p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">
          {t.linesTitle} ({titleSuffix})
        </span>
        <span className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {mandateName}
          {chips.map((chip) => (
            <Badge key={chip} variant="secondary">
              {chip}
            </Badge>
          ))}
          {badge}
        </span>
      </div>
      {subHeader && <p className="text-xs text-muted-foreground">{subHeader}</p>}

      <div className="space-y-2.5">
        {lines.map((line) => {
          const outside = line.status !== "within";
          // QL-730 B7 — held at the bank as a position the platform has no
          // product for: kept or sold, never bought.
          const suffix = !line.held
            ? ` — ${line.unholdableReason ? unholdableText(line.unholdableReason, t) : t.notHeld}`
            : line.heldVia === "unmatched_holding"
              ? ` — ${t.heldAtBank}`
              : "";
          return (
            <div
              key={`${line.isin ?? "name"}:${line.name ?? ""}:${line.order}`}
              className="grid grid-cols-[1rem_minmax(0,1fr)_5rem] @2xl:grid-cols-[1rem_minmax(0,2fr)_minmax(0,3fr)_5rem_minmax(0,2fr)] items-center gap-x-3 gap-y-1 text-sm"
            >
              <StatusIcon line={line} neutral={noMandate} />
              <span className="min-w-0 break-words" title={line.isin ?? undefined}>
                {line.name ?? line.isin ?? "—"}
                {suffix && <span className="text-muted-foreground">{suffix}</span>}
              </span>
              {/* The figure carries the verdict's colour: red outside, green within (QL-664). */}
              <span
                className={cn(
                  "text-right tabular-nums font-medium order-1 @2xl:order-3",
                  !noMandate && (outside ? "text-destructive" : evaluated && "text-success"),
                )}
              >
                {formatMandatePct(line.combinedPct)}
              </span>
              <span className="col-start-2 @2xl:col-start-auto order-2 @2xl:order-2">
                <MandateRangeBar mode={mode} line={line} bands={!noMandate} />
              </span>
              <span className="text-right tabular-nums text-muted-foreground text-xs @2xl:text-sm col-start-3 @2xl:col-start-auto order-3 @2xl:order-4 whitespace-nowrap">
                {noMandate
                  ? "—"
                  : mode === "fixed"
                    ? formatMandatePct(line.weightPct)
                    : `${formatMandatePct(line.minPct)}–${formatMandatePct(line.maxPct)} · ${t.bandDefault} ${formatMandatePct(line.defaultPct)}`}
              </span>
            </div>
          );
        })}
        {unlisted.map((u) => (
          <div
            key={`unlisted:${u.isin ?? "name"}:${u.name}`}
            className="grid grid-cols-[1rem_minmax(0,1fr)_5rem] @2xl:grid-cols-[1rem_minmax(0,2fr)_minmax(0,3fr)_5rem_minmax(0,2fr)] items-center gap-x-3 gap-y-1 text-sm"
          >
            <XCircle className="h-4 w-4 text-destructive shrink-0" />
            <span className="min-w-0 break-words" title={u.isin ?? undefined}>
              {u.kind === "unmatched_remainder" || u.unnamed ? t.unmatchedHoldings : u.name}
              <span className="text-muted-foreground"> — {t.unlistedTitle.toLowerCase()}</span>
            </span>
            <span className="hidden @2xl:block @2xl:order-2" />
            <span className="text-right tabular-nums font-medium text-destructive order-1 @2xl:order-3">
              {formatMandatePct(u.combinedPct)}
            </span>
            <span className="hidden @2xl:block text-right text-muted-foreground @2xl:order-4">
              —
            </span>
          </div>
        ))}
      </div>

      {/* No mandate and no products (QL-760): no dot is drawn, so there is no
          legend; a verdict (a cause other than the missing mandate) then reads
          left-aligned on its own instead of floating right of an empty span. */}
      {noMandate && lines.length === 0 ? (
        verdict && <p className={cn("pt-1 text-sm", VERDICT_TONE[verdict.tone])}>{verdict.text}</p>
      ) : (
        // The legend on the left; the verdict sentence on the right (QL-664).
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-1 pt-1 text-xs">
          <span className="flex flex-wrap items-center gap-4 text-muted-foreground">
            {/* Fixed mode draws no band, so a "permitted range" item would mislead. */}
            {!noMandate && mode === "min_max" && (
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-5 rounded-full bg-muted-foreground/30" />
                {t.legendRange}
              </span>
            )}
            {!noMandate && (
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-3 w-0.5 bg-muted-foreground/70" />
                {t.legendDefault}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-foreground" />
              {noMandate
                ? t.legendThisPortfolio
                : scope === "portfolio"
                  ? t.legendWeight
                  : t.legendCombined}
            </span>
          </span>
          {verdict && (
            <span className={cn("text-sm text-right sm:max-w-[60%]", VERDICT_TONE[verdict.tone])}>
              {verdict.text}
            </span>
          )}
        </div>
      )}

      {selectionSection}
      {decisionSection}
    </div>
  );
}
